import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useListOpportunitiesQuery } from "@/state/opportunitiesApi";

export default function FeaturedOpportunities() {
  const { t } = useTranslation();
  const { data, isLoading } = useListOpportunitiesQuery({ featured: true, perPage: 2 });
  const opportunities = data?.data ?? [];

  return (
    <section className="py-28 bg-white">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-16">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
          <div>
            <span className="inline-block text-xs font-bold tracking-[0.2em] text-[#00C2C2] uppercase mb-3">
              {t("opportunitiesTag")}
            </span>
            <h2 className="text-4xl lg:text-5xl font-extrabold text-[#0A1628] leading-tight">
              {t("opportunitiesTitle")}{" "}
              <span className="text-[#00C2C2]">{t("opportunitiesTitleAccent")}</span>
            </h2>
          </div>
          <p className="text-gray-500 max-w-sm text-sm leading-relaxed">
            {t("opportunitiesDesc")}
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div className="bg-[#0A1628] rounded-2xl p-8 flex flex-col justify-between min-h-[320px]">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#00C2C2]/10 mb-4">
                <div className="w-2 h-2 rounded-full bg-[#00C2C2]" />
                <span className="text-xs font-bold text-[#00C2C2] uppercase tracking-wider">
                  {t("opportunitiesTag")}
                </span>
              </div>
              <h2 className="text-white text-3xl font-bold leading-tight mb-4">
                {t("opportunitiesTitle")}
                <br />
                <span className="text-[#00C2C2]">{t("opportunitiesTitleAccent")}</span>
              </h2>
              <p className="text-white/70 text-sm leading-relaxed">
                {t("opportunitiesDesc")}
              </p>
            </div>
            <Link
              to="/business"
              className="mt-8 inline-flex items-center gap-2 bg-[#00C2C2] text-white text-sm font-semibold px-6 py-3 rounded-full hover:bg-[#00AAAA] transition-all whitespace-nowrap w-fit"
            >
              {t("viewAll")} <i className="ri-arrow-right-line" />
            </Link>
          </div>

          {isLoading
            ? Array.from({ length: 2 }).map((_, i) => (
                <div key={i} className="animate-pulse bg-[#F5FAFA] rounded-2xl border border-gray-100 p-6 min-h-[320px]">
                  <div className="h-4 bg-gray-200 rounded w-1/3 mb-4" />
                  <div className="h-5 bg-gray-200 rounded w-3/4 mb-3" />
                  <div className="h-3 bg-gray-200 rounded w-full mb-2" />
                  <div className="h-3 bg-gray-200 rounded w-5/6 mb-2" />
                  <div className="h-3 bg-gray-200 rounded w-4/6" />
                </div>
              ))
            : opportunities.map((opp) => (
                <div
                  key={opp.id}
                  className="group bg-[#F5FAFA] rounded-2xl overflow-hidden border border-gray-100 hover:border-[#00C2C2]/30 hover:-translate-y-1 transition-all duration-300 cursor-default"
                >
                  <div className="p-6 flex flex-col gap-4 h-full">
                    <div className="flex items-center gap-2">
                      <span className="flex items-center gap-1.5 text-xs font-semibold text-[#00C2C2] bg-[#00C2C2]/10 px-2.5 py-1 rounded-full">
                        <span className="block w-1.5 h-1.5 rounded-full bg-[#00C2C2]" />
                        {opp.status}
                      </span>
                      {opp.investmentLevel && (
                        <span className="text-xs text-gray-400 font-medium">{opp.investmentLevel}</span>
                      )}
                    </div>

                    <h3 className="text-[#0A1628] font-bold text-base leading-snug">{opp.title}</h3>
                    <p className="text-gray-500 text-sm leading-relaxed flex-1">{opp.description}</p>

                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="flex items-center gap-1 text-xs text-gray-500 bg-white px-2.5 py-1 rounded-full border border-gray-100">
                        <i className="ri-map-pin-2-line" /> {opp.country}
                      </span>
                      <span className="flex items-center gap-1 text-xs text-gray-500 bg-white px-2.5 py-1 rounded-full border border-gray-100">
                        <i className="ri-price-tag-3-line" /> {opp.sector}
                      </span>
                    </div>

                    <Link
                      to={`/business/opportunity/${opp.id}`}
                      className="mt-1 inline-flex items-center gap-2 bg-[#00C2C2] text-white text-sm font-semibold px-5 py-2.5 rounded-full hover:bg-[#00AAAA] transition-all whitespace-nowrap w-fit"
                    >
                      {t("oppExpressInterest")} <i className="ri-arrow-right-line" />
                    </Link>
                  </div>
                </div>
              ))}
        </div>
      </div>
    </section>
  );
}
